import React, { useEffect } from 'react';
import Icon from './Icon';
import ImageCarousel from './ImageCarousel';

/**
 * Lightbox component for displaying portfolio images
 * @param {Object} props - Component props
 * @param {Object} props.item - Portfolio item to display
 * @param {Function} props.onClose - Function to close the lightbox
 * @returns {React.ReactElement} Lightbox component
 */
const Lightbox = ({ item, onClose }) => {
  // Prevent scrolling when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Add escape key listener
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Close when clicking outside the image
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!item) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative max-w-5xl w-full">
        <button 
          className="absolute -top-12 right-0 text-white hover:text-brand-blue-600 transition-colors"
          onClick={onClose}
          aria-label="Close lightbox"
        >
          <Icon name="x-close" size={32} />
        </button>
        
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="relative">
            {item.type === 'video' && item.videoEmbedUrl ? (
              <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                  src={item.videoEmbedUrl}
                  loading="lazy"
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen={true}
                  title={item.title}
                />
              </div>
            ) : item.hasCarousel && item.carouselImages ? (
              <ImageCarousel 
                images={item.carouselImages} 
                title={item.title} 
              />
            ) : (
              <img 
                src={item.full} 
                alt={item.title} 
                className="w-full h-auto"
              />
            )}
          </div>
          
          <div className="p-4">
            <h3 className="text-xl font-bold mb-2">{item.title}</h3>
            {item.caption && (
              <p className="text-gray-600">{item.caption}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
